import React from 'react';
import { Button, Typography } from 'antd';
import { BulbOutlined, EditOutlined } from '@ant-design/icons';
import css from './WarmPathNudge.module.scss';

const { Text } = Typography;

interface WarmPathNudgeProps {
  companyName: string;
  linkedinConnectionsUrl?: string | null;
  // Analytics only — navigation is the anchor's own href (new tab).
  onSeeWhoClick: () => void;
  onDraftIntro: () => void;
  // When the nudge IS the stage (Apply through LinkedIn), promote the see-who
  // CTA to the visually primary starting action. Default false: rendered as an
  // aside elsewhere, it stays quiet.
  prominent?: boolean;
}

/**
 * The warm-path nudge: before applying cold, check whether you already know
 * someone at the company. Amber on purpose — amber = "a human you can reach"
 * in this app's design language (see the dashboard's ReachedOutNode).
 * The NY Fed stat is measured (peer-reviewed), so it's stated plainly.
 */
const WarmPathNudge: React.FC<WarmPathNudgeProps> = ({
  companyName,
  linkedinConnectionsUrl,
  onSeeWhoClick,
  onDraftIntro,
  prominent = false,
}) => (
  <div className={css.nudge}>
    <div className={css.heading}>
      <BulbOutlined />
      <Text strong>People you know are your best shot.</Text>
    </div>
    <div className={css.stat}>
      Referred candidates are ~6% of applicants — but ~29% of hires.
    </div>
    <div className={css.ctas}>
      {linkedinConnectionsUrl && (
        <Button
          type={prominent ? 'primary' : 'default'}
          className={css.seeWho}
          href={linkedinConnectionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onSeeWhoClick}
          aria-label={`See who you know at ${companyName} — opens LinkedIn in a new tab`}
        >
          {/* The company name is unbounded, so this label wraps (see the SCSS).
              A non-breaking space keeps the ↗ attached to the last word rather
              than letting it orphan onto a line of its own. */}
          See who you know at {companyName}&nbsp;↗
        </Button>
      )}
      <Button icon={<EditOutlined />} onClick={onDraftIntro}>
        Draft an intro request
      </Button>
    </div>
  </div>
);

export default WarmPathNudge;
